import { ELocalStorageKey } from '@/enums/ELocalStorageKey';
import { EKeyCode } from '@/enums/e_key_code';
import { use_effect } from '@/hooks/_base/use_effect';
import { use_slice_user } from '@/hooks/store/use_slices';
import { WATCHER_SHORTCUT__base } from '@/pages/_base/shortcuts/_watchers/WATCHER_SHORTCUT__base';
import { assert_not_null } from '@shared/asserts/utils_asserts';
import { ENUM__ui_theme } from '@shared/enums/ENUM__ui_theme';
import { createContext, useContext, useState } from 'react';

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: string;
  storageKey?: string;
};

type ThemeProviderState = {
  theme: string;
  setTheme: (theme: string) => void;
};

const initialState: ThemeProviderState = {
  theme: ENUM__ui_theme.SYSTEM,
  setTheme: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({ children, defaultTheme, storageKey = ELocalStorageKey.THEME, ...props }: ThemeProviderProps) {
  const { user_settings } = use_slice_user(['user_settings']);

  const [theme, set_theme] = useState(localStorage.getItem(storageKey) || ENUM__ui_theme.SYSTEM);

  /*
   * EFFECTS
   */
  use_effect(() => on_theme_changed(user_settings.ui_theme || ENUM__ui_theme.DARK), [user_settings.ui_theme]);

  use_effect(() => {
    const root = window.document.documentElement;

    root.classList.remove(ENUM__ui_theme.LIGHT, ENUM__ui_theme.DARK);

    let class_name_to_add = theme;

    if (theme === ENUM__ui_theme.SYSTEM) {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? ENUM__ui_theme.DARK : ENUM__ui_theme.LIGHT;

      class_name_to_add = systemTheme;
    }

    root.classList.add(class_name_to_add);
  }, [theme]);

  /*
   * EVENTS
   */

  function on_theme_changed(value: (typeof ENUM__ui_theme)[keyof typeof ENUM__ui_theme]) {
    const newValue: string = value;

    set_theme(newValue);

    localStorage.setItem(storageKey, newValue);
  }

  /*
   * EVENTS
   */

  function on_shortcut_pressed() {
    let valueNew = theme === ENUM__ui_theme.DARK ? ENUM__ui_theme.LIGHT : ENUM__ui_theme.DARK;

    set_theme(valueNew);

    localStorage.setItem(ELocalStorageKey.THEME, valueNew);

    return true;
  }

  /*
   * HTML
   */
  return (
    <>
      <WATCHER_SHORTCUT__base key_codes={[EKeyCode.P]} {...{ on_shortcut_pressed }} />
      <ThemeProviderContext.Provider {...props} value={{ theme, setTheme: set_theme }}>
        {children}
      </ThemeProviderContext.Provider>
    </>
  );
}

export function use_theme() {
  const context = useContext(ThemeProviderContext);

  //useTheme must be used within a ThemeProvider
  assert_not_null(context);

  return context;
}
